import React, { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Alert, alpha, Box, Button, Chip, Divider, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { forgetCallData } from '../calls/LogCallForm';
import { ContactPager } from '../contacts/ContactListControls';
import { DEFAULT_PAGE_SIZE } from '../contacts/contactList';
import { CallLogFilters } from './CallLogFilters';
import { CallLogTable, TONES } from './CallLogTable';
import { CorrectCallDialog } from './CorrectCallDialog';
import {
  ALL_OUTCOMES,
  CallLogRow,
  CallLogState,
  callOutcomeLabel,
  describeCallCount,
  describeLogError,
  EMPTY_LOG_STATE,
  hasLogFilters,
  outcomeTone,
  parseLogState,
  toLogParams,
  withLogChange,
} from './callLog';
import { useCallLogPage } from './useCallLogPage';

const n = (value: number) => value.toLocaleString('en-US');

/** Rows in the table's shape while the first page loads. */
const LoadingRows = () => (
  <Box role="status" aria-label="Loading calls…" sx={{ px: 2, py: 0.5 }}>
    {Array.from({ length: 8 }, (_, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, borderBottom: '1px solid #eef1f5' }}>
        <Skeleton variant="text" sx={{ flex: '0 0 12%' }} />
        <Skeleton variant="text" sx={{ flex: '0 0 18%' }} />
        <Skeleton variant="rounded" width={80} height={20} />
        <Skeleton variant="text" sx={{ flex: '0 0 12%' }} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
      </Box>
    ))}
  </Box>
);

/**
 * The call log (card 21.6): every call on record, logged in this panel or
 * migrated from NationBuilder, newest first. Filter by the notes, one person,
 * caller, outcome, how, direction, when, follow-up and where it was recorded;
 * the counts by outcome above the table are the outcome filter. A row opens
 * to show the whole call; a call the admin logged here can be corrected.
 *
 * Every choice is in the URL, so the way back from a contact lands on the log
 * as it was left. Requests: on opening and on a filter change, a page with the
 * total and the counts by outcome and caller; on paging, the page alone.
 */
export const CrmCallLog = () => {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseLogState(params), [params]);
  const client = useApolloClient();
  const toast = useToast();
  const [correcting, setCorrecting] = useState<CallLogRow | null>(null);

  // "Now" for the date ranges and "follow-up due": fixed while the filters stay
  // the same, so paging does not change the request's variables.
  const filterParams = toLogParams({ ...state, page: 0, pageSize: DEFAULT_PAGE_SIZE }).toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => dayjs(), [filterParams]);

  const show = useCallback(
    // A notes search typed a letter at a time replaces the history entry rather than adding one each pause.
    (next: CallLogState, replace = false) => setParams(toLogParams(next), { replace }),
    [setParams],
  );
  const change = useCallback((partial: Partial<CallLogState>) => show(withLogChange(state, partial), 'notes' in partial), [show, state]);

  const { rows, loading, error, hasNextPage, counts, reset } = useCallLogPage(state, now);
  const myAdminId = counts?.byCaller.find((caller) => caller.isYou)?.adminId ?? null;
  const filtered = hasLogFilters(state);
  const firstLoad = loading && rows.length === 0;

  const toggleOutcome = (outcome: CallLogState['outcomes'][number]) =>
    change({ outcomes: state.outcomes.includes(outcome) ? state.outcomes.filter((o) => o !== outcome) : [...state.outcomes, outcome] });

  // In a fixed order, so chips do not jump as counts change: every outcome with calls, and any chosen.
  const outcomeChips = ALL_OUTCOMES.map((outcome) => ({
    outcome,
    count: counts?.byOutcome.find((o) => o.outcome === outcome)?.count ?? 0,
  })).filter((chip) => chip.count > 0 || state.outcomes.includes(chip.outcome));

  const closeCorrection = (changed: boolean) => {
    setCorrecting(null);
    if (!changed) return;
    // The outcome, follow-up or notes may have moved the call between filters and counts.
    forgetCallData(client.cache);
    reset();
    toast.success('Call corrected.');
  };

  return (
    <CrmPageFrame title="Call log" description="Every call on record: logged in this panel or migrated from NationBuilder.">
      <Item sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <CallLogFilters
            state={state}
            callers={counts?.byCaller}
            onChange={change}
            onClear={() => show({ ...EMPTY_LOG_STATE, pageSize: state.pageSize })}
          />
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
          <Typography
            variant="h6"
            component="p"
            aria-live="polite"
            sx={{ fontSize: '1.125rem', fontVariantNumeric: 'tabular-nums', mr: 1 }}
          >
            {counts ? describeCallCount(counts.total) : loading ? 'Counting…' : ''}
          </Typography>
          <Box role="group" aria-label="Calls by outcome" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {outcomeChips.map(({ outcome, count }) => {
              const on = state.outcomes.includes(outcome);
              const tone = TONES[outcomeTone(outcome)];
              return (
                <Tooltip
                  key={outcome}
                  describeChild
                  title={
                    outcome === 'UNKNOWN'
                      ? "NationBuilder's history: its outcome codes are not mapped yet."
                      : on
                        ? 'Showing these. Click to stop.'
                        : 'Click to show only these. Click others to add them.'
                  }
                >
                  <Chip
                    size="small"
                    clickable
                    aria-pressed={on}
                    onClick={() => toggleOutcome(outcome)}
                    label={`${callOutcomeLabel(outcome)} ${n(count)}`}
                    sx={{
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: on ? '#fff' : tone.color,
                      bgcolor: on ? tone.color : alpha(tone.color, 0.04),
                      border: '1px solid',
                      borderColor: on ? tone.color : tone.border,
                      '&:hover': { bgcolor: on ? tone.color : tone.bg },
                      '&.MuiChip-clickable:hover': { bgcolor: on ? alpha(tone.color, 0.9) : tone.bg },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        {loading ? <LinearProgress aria-label="Loading calls" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {describeLogError(error)}
          </Alert>
        )}

        {rows.length > 0 && (
          <CallLogTable
            rows={rows}
            now={now}
            myAdminId={myAdminId}
            stale={loading}
            onCorrect={setCorrecting}
            onPerson={(person) => change({ person })}
          />
        )}

        {firstLoad && <LoadingRows />}

        {!firstLoad && !error && rows.length === 0 && (
          <Box sx={{ py: 7, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
            <Box
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                mb: 1,
              })}
            >
              <PhoneInTalkOutlinedIcon />
            </Box>
            <Typography variant="h6" component="p">
              {filtered ? 'No calls match' : 'No calls yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              {filtered
                ? 'No call fits these filters. Try removing one, or a wider time.'
                : 'Calls appear here as they are logged, from a contact\'s page or the call queue.'}
            </Typography>
            {filtered && (
              <Button variant="outlined" sx={{ mt: 1 }} onClick={() => show({ ...EMPTY_LOG_STATE, pageSize: state.pageSize })}>
                Clear filters
              </Button>
            )}
          </Box>
        )}

        <Divider />
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          <ContactPager
            page={state.page}
            pageSize={state.pageSize}
            rowsOnPage={rows.length}
            hasNextPage={hasNextPage}
            total={counts ? { count: counts.total, exact: true } : undefined}
            disabled={loading}
            onPage={(page) => show({ ...state, page })}
            onPageSize={(pageSize) => change({ pageSize })}
          />
        </Box>
      </Item>

      {correcting && <CorrectCallDialog call={correcting} onClose={closeCorrection} />}
    </CrmPageFrame>
  );
};
